import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ChangeRequestDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { ChangeRequestRegisterWithUserFieldsRow } from '@risksmart-app/drizzle/src/dto';
import type { GetChangeRequestsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetChangeRequestsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import { mapChangeRequestRegisterRowsToGraphQl } from '@/hooks/queries/change-request/mapChangeRequestRegisterRowsToGraphQl';
import { CHANGE_REQUEST_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';

/**
 * COI-691's single non-additive change: `currentUserId` was a query arg used
 * only to fill the GraphQL variable, which forced every caller to pass ambient
 * context. It is resolved from `useRisksmartUser()` here instead, so callers
 * pass dashboard filters only.
 */
const useChangeRequestsGraphqlVariables = (args: UseGetChangeRequestsArgs) => {
  const { user } = useRisksmartUser();

  const { where } = buildDashboardWhere({
    filters: args,
    datePredicates: CHANGE_REQUEST_DATE_PREDICATES,
  });

  return {
    currentUserId: user?.userId ?? '',
    ...(where ? { where } : {}),
  };
};

/**
 * Change request declares no tag or department filter, and the dashboard's
 * eight ownership flags have no server leg here — the register's actionability
 * narrowing is the client-side `isActiveApprover` pass instead. So the
 * dashboard half is dates only.
 *
 * "Client-side" is about which readable rows the dashboard chooses to surface,
 * NOT about who may read them. The row set itself is bounded server-side by
 * perm_v2 + RLS on the parent entity (`getChangeRequestsRegisterProcessor`);
 * no argument to this hook can widen it.
 */
export type UseGetChangeRequestsArgs = Pick<
  DashboardFilterArgs<ChangeRequestDashboardDateField>,
  'dateRange'
>;

export const useGetChangeRequests = createQueryHook<
  UseGetChangeRequestsArgs,
  ChangeRequestRegisterWithUserFieldsRow[],
  GetChangeRequestsQuery
>({
  trpcQueryOptions: (trpc, { dateRange }) =>
    trpc.frontend.changeRequest.register.queryOptions(
      toTrpcDashboardFilterInput({ dateRange })
    ),
  mapTrpcDataToGraphQL: mapChangeRequestRegisterRowsToGraphQl,
  graphqlDocument: GetChangeRequestsDocument,
  graphqlVariables: useChangeRequestsGraphqlVariables,
});
